/*
 This file is part of GNU Taler
 (C) 2021-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 *
 * @author Sebastian Javier Marchano (sebasjm)
 */

import {
  MerchantAccountKycStatusSimplified,
  MerchantPersona
} from "@gnu-taler/taler-util";
import { useTranslationContext } from "@gnu-taler/web-util/browser";
import { ComponentChildren, Fragment, h, VNode } from "preact";
import { useSessionContext } from "../../context/session.js";
import { useSettingsContext } from "../../context/settings.js";
import { UIElement, usePreference } from "../../hooks/preference.js";
import { LangSelector } from "./LangSelector.js";
import { useInstanceKYCSimplifiedWorstStatusLongPolling } from "../../hooks/instance.js";

const TALER_SCREEN_ID = 17;
// const GIT_HASH = typeof __GIT_HASH__ !== "undefined" ? __GIT_HASH__ : undefined;
const VERSION = typeof __VERSION__ !== "undefined" ? __VERSION__ : undefined;

interface Props {
  mobile?: boolean;
}


export function Sidebar({ mobile }: Props): VNode {
  const { i18n } = useTranslationContext();
  const { state, logOut, config } = useSessionContext();
  const worstKycStatus = useInstanceKYCSimplifiedWorstStatusLongPolling();

  const [{ persona }] = usePreference();
  const hideKycMenuItem =
    worstKycStatus === MerchantAccountKycStatusSimplified.OK &&
    persona !== "expert";

  const isLoggedIn = state.status === "loggedIn";
  const hasToken = isLoggedIn && state.token !== undefined;

  const { isTestingEnvironment } = useSettingsContext();

  return (
    <aside
      class="aside is-placed-left is-expanded"
      style={{ overflowY: "scroll" }}
    >
      {mobile && (
        <div
          class="footer"
          onClick={(e) => {
            return e.stopImmediatePropagation();
          }}
        >
          <LangSelector />
        </div>
      )}
      <div class="aside-tools">
        <div class="aside-tools-label">
          <div>
            <b>Taler</b> Backoffice
          </div>
          <div
            class="is-size-7 has-text-right"
            style={{ lineHeight: 0, marginTop: -10 }}
          >
            {VERSION} ({config.version})
          </div>
        </div>
      </div>
      <div class="menu is-menu-main">
        {!isTestingEnvironment ? undefined : (
          <div style="color:orangered">TESTING ENVIRONMENT</div>
        )}
        {isLoggedIn ? (
          <Fragment>
            <ul class="menu-list">
              <HtmlPersonaFlag
                htmlElement="li"
                point={UIElement.sidebar_orders}
              >
                <a href={"#/orders"} class="has-icon">
                  <i class="icon mdi mdi-cash-register" />
                  <span class="menu-item-label">
                    <i18n.Translate>Orders</i18n.Translate>
                  </span>
                </a>
              </HtmlPersonaFlag>
              <HtmlPersonaFlag
                htmlElement="li"
                point={UIElement.sidebar_inventory}
              >
                <a href={"#/inventory"} class="has-icon">
                  <i class="icon mdi mdi-shopping" />

                  <span class="menu-item-label">
                    <i18n.Translate>Inventory</i18n.Translate>
                  </span>
                </a>
              </HtmlPersonaFlag>
              <HtmlPersonaFlag
                htmlElement="li"
                point={UIElement.sidebar_categories}
              >
                <a href={"#/category"} class="has-icon">
                  <i class="icon mdi mdi-label-outline" />

                  <span class="menu-item-label">
                    <i18n.Translate>Categories</i18n.Translate>
                  </span>
                </a>
              </HtmlPersonaFlag>
              <HtmlPersonaFlag
                htmlElement="li"
                point={UIElement.sidebar_wireTransfers}
              >
                <a href={"#/transfers"} class="has-icon">
                  <i class="icon mdi mdi-arrow-left-right" />

                  <span class="menu-item-label">
                    <i18n.Translate>Wire transfers</i18n.Translate>
                  </span>
                </a>
              </HtmlPersonaFlag>
              <HtmlPersonaFlag
                htmlElement="li"
                point={UIElement.sidebar_templates}
              >
                <a href={"#/templates"} class="has-icon">
                  <i class="icon mdi mdi-qrcode" />

                  <span class="menu-item-label">
                    <i18n.Translate>Templates</i18n.Translate>
                  </span>
                </a>
              </HtmlPersonaFlag>
              <HtmlPersonaFlag htmlElement="li" point={UIElement.sidebar_group}>
                <a href={"#/groups"} class="has-icon">
                  <i class="icon mdi mdi-group" />

                  <span class="menu-item-label">
                    <i18n.Translate>Product groups</i18n.Translate>
                  </span>
                </a>
              </HtmlPersonaFlag>
              <HtmlPersonaFlag htmlElement="li" point={UIElement.sidebar_pots}>
                <a href={"#/pots"} class="has-icon">
                  <i class="icon mdi mdi-pot" />

                  <span class="menu-item-label">
                    <i18n.Translate>Money pots</i18n.Translate>
                  </span>
                </a>
              </HtmlPersonaFlag>
              <HtmlPersonaFlag
                htmlElement="li"
                point={UIElement.sidebar_reports}
              >
                <a href={"#/reports"} class="has-icon">
                  <i class="icon mdi mdi-paperclip" />

                  <span class="menu-item-label">
                    <i18n.Translate>Reports</i18n.Translate>
                  </span>
                </a>
              </HtmlPersonaFlag>
              <HtmlPersonaFlag
                htmlElement="li"
                point={UIElement.sidebar_statistics}
              >
                <a href={"#/statistics"} class="has-icon">
                  <i class="icon mdi mdi-counter" />

                  <span class="menu-item-label">
                    <i18n.Translate>Statistics</i18n.Translate>
                  </span>
                </a>
              </HtmlPersonaFlag>
              <HtmlPersonaFlag
                htmlElement="li"
                point={UIElement.sidebar_tokenFamilies}
              >
                <a href={"#/tokenfamilies"} class="has-icon">
                  <span class="menu-item-label">
                    <i class="icon mdi mdi-clock" />

                    <i18n.Translate>Subscriptions and Discounts</i18n.Translate>
                  </span>
                </a>
              </HtmlPersonaFlag>
            </ul>
            <p class="menu-label">
              <i18n.Translate>Configuration</i18n.Translate>
            </p>
            <ul class="menu-list">
              <HtmlPersonaFlag
                htmlElement="li"
                point={UIElement.sidebar_bankAccounts}
              >
                <a href={"#/bank"} class="has-icon">
                  <i class="icon mdi mdi-bank" />

                  <span class="menu-item-label">
                    <i18n.Translate>Bank account</i18n.Translate>
                  </span>
                </a>
              </HtmlPersonaFlag>
              {hideKycMenuItem ? undefined : (
                <HtmlPersonaFlag
                  htmlElement="li"
                  point={UIElement.sidebar_kycStatus}
                  class={
                    worstKycStatus ===
                    MerchantAccountKycStatusSimplified.WARNING
                      ? "is-warning"
                      : worstKycStatus ===
                          MerchantAccountKycStatusSimplified.ERROR
                        ? "is-error"
                        : worstKycStatus ===
                            MerchantAccountKycStatusSimplified.ACTION_REQUIRED
                          ? "is-warning"
                          : undefined
                  }
                >
                  <a
                    href={"#/kyc"}
                    class="has-icon"
                    style={
                      worstKycStatus ===
                      MerchantAccountKycStatusSimplified.WARNING
                        ? {
                            backgroundColor: "darkorange",
                            color: "black",
                          }
                        : worstKycStatus ===
                            MerchantAccountKycStatusSimplified.ERROR
                          ? {
                              backgroundColor: "#e93c3c",
                              color: "black",
                            }
                          : worstKycStatus ===
                              MerchantAccountKycStatusSimplified.ACTION_REQUIRED
                            ? {
                                backgroundColor: "darkorange",
                                color: "black",
                              }
                            : undefined
                    }
                  >
                    <i class="icon mdi mdi-account-check" />

                    <span class="menu-item-label">KYC Status</span>
                  </a>
                </HtmlPersonaFlag>
              )}
              <HtmlPersonaFlag
                htmlElement="li"
                point={UIElement.sidebar_otpDevices}
              >
                <a href={"#/otp-devices"} class="has-icon">
                  <i class="icon mdi mdi-lock" />

                  <span class="menu-item-label">
                    <i18n.Translate>OTP Devices</i18n.Translate>
                  </span>
                </a>
              </HtmlPersonaFlag>
              <HtmlPersonaFlag
                htmlElement="li"
                point={UIElement.sidebar_webhooks}
              >
                <a href={"#/webhooks"} class="has-icon">
                  <i class="icon mdi mdi-webhook" />

                  <span class="menu-item-label">
                    <i18n.Translate>Webhooks</i18n.Translate>
                  </span>
                </a>
              </HtmlPersonaFlag>
              <HtmlPersonaFlag
                htmlElement="li"
                point={UIElement.sidebar_settings}
              >
                <a href={"#/settings"} class="has-icon">
                  <i class="icon mdi mdi-square-edit-outline" />

                  <span class="menu-item-label">
                    <i18n.Translate>Settings</i18n.Translate>
                  </span>
                </a>
              </HtmlPersonaFlag>
              <HtmlPersonaFlag
                htmlElement="li"
                point={UIElement.sidebar_password}
              >
                <a href={"#/password"} class="has-icon">
                  <i class="icon mdi mdi-security" />

                  <span class="menu-item-label">
                    <i18n.Translate>Password</i18n.Translate>
                  </span>
                </a>
              </HtmlPersonaFlag>
              <HtmlPersonaFlag
                htmlElement="li"
                point={UIElement.sidebar_accessTokens}
              >
                <a href={"#/access-token"} class="has-icon">
                  <i class="icon mdi mdi-key" />

                  <span class="menu-item-label">
                    <i18n.Translate>Access tokens</i18n.Translate>
                  </span>
                </a>
              </HtmlPersonaFlag>
            </ul>
          </Fragment>
        ) : undefined}
        <p class="menu-label">
          <i18n.Translate>Connection</i18n.Translate>
        </p>
        <ul class="menu-list">
          <li>
            <a class="has-icon is-state-info is-hoverable" href="#/interface">
              <i class="icon mdi mdi-newspaper" />
              <span class="menu-item-label">
                <i18n.Translate>Personalization</i18n.Translate>
              </span>
            </a>
          </li>
          <li>
            <div class="has-icon">
              <i class="icon mdi mdi-web" style={{ width: "3rem" }} />
              <span class="menu-item-label">{state.backendUrl.hostname}</span>
            </div>
          </li>
          <li>
            <div class="has-icon">
              <span class="icon" style={{ width: "3rem" }}>
                ID
              </span>
              <span class="menu-item-label">{state.instance}</span>
            </div>
          </li>
          {state.isAdmin && (
            <Fragment>
              <p class="menu-label">
                <i18n.Translate>Instances</i18n.Translate>
              </p>
              <li>
                <a href={"#/instance/new"} class="has-icon">
                  <i class="icon mdi mdi-plus" />

                  <span class="menu-item-label">
                    <i18n.Translate>New</i18n.Translate>
                  </span>
                </a>
              </li>
              <li>
                <a href={"#/instances"} class="has-icon">
                  <i class="icon mdi mdi-format-list-bulleted" />

                  <span class="menu-item-label">
                    <i18n.Translate>List</i18n.Translate>
                  </span>
                </a>
              </li>
            </Fragment>
          )}
          {hasToken ? (
            <li>
              <a
                class="has-icon is-state-info is-hoverable"
                onClick={(e): void => {
                  logOut();
                  e.preventDefault();
                }}
              >
                <i class="icon mdi mdi-logout default" />

                <span class="menu-item-label">
                  <i18n.Translate>Log out</i18n.Translate>
                </span>
              </a>
            </li>
          ) : undefined}
        </ul>
      </div>
    </aside>
  );
}

type ElementMap = {
  [sdt in UIElement]: boolean;
};
const ALL_ELEMENTS = Object.values(UIElement).reduce((prev, ui) => {
  prev[ui as UIElement] = true;
  return prev;
}, {} as ElementMap);

export function getAvailableForPersona(p: MerchantPersona): ElementMap {
  switch (p) {
    case "tester":
      return ALL_ELEMENTS;
    case "expert":
      return {
        [UIElement.sidebar_orders]: true,
        [UIElement.sidebar_templates]: true,
        [UIElement.sidebar_kycStatus]: true,
        [UIElement.sidebar_bankAccounts]: true,
        [UIElement.sidebar_settings]: true,
        [UIElement.sidebar_password]: true,
        [UIElement.sidebar_accessTokens]: true,
        [UIElement.sidebar_categories]: true,
        [UIElement.sidebar_group]: true,
        [UIElement.sidebar_pots]: true,
        [UIElement.sidebar_reports]: true,
        [UIElement.sidebar_webhooks]: true,
        [UIElement.sidebar_wireTransfers]: true,
        [UIElement.sidebar_inventory]: true,
        [UIElement.sidebar_otpDevices]: true,
        [UIElement.action_createOrderManually]: true,
        [UIElement.option_advanceInstanceSettings]: true,
        [UIElement.option_advanceOrderCreation]: true,
        [UIElement.option_otpDevicesOnTemplate]: true,
        [UIElement.option_paymentTimeoutOnTemplate]: true,
        [UIElement.action_useRevenueApi]: true,
        [UIElement.option_inventoryTaxes]: true,
        [UIElement.sidebar_statistics]: false,

        [UIElement.sidebar_discounts]: false,
        [UIElement.sidebar_subscriptions]: false,
        [UIElement.sidebar_tokenFamilies]: false,
        [UIElement.option_ageRestriction]: false,
        [UIElement.option_refreshableScopes]: false,
        [UIElement.option_advanceProductTaxes]: false,
        [UIElement.option_exraWireSubject]: true,
      };
    case "offline-vending-machine":
      return {
        [UIElement.sidebar_orders]: true,
        [UIElement.sidebar_templates]: true,
        [UIElement.sidebar_kycStatus]: true,
        [UIElement.sidebar_bankAccounts]: true,
        [UIElement.sidebar_settings]: true,
        [UIElement.sidebar_password]: true,

        [UIElement.sidebar_accessTokens]: false,
        [UIElement.sidebar_categories]: false,
        [UIElement.sidebar_group]: false,
        [UIElement.sidebar_pots]: false,
        [UIElement.sidebar_reports]: false,
        [UIElement.sidebar_discounts]: false,
        [UIElement.sidebar_subscriptions]: false,
        [UIElement.sidebar_tokenFamilies]: false,
        [UIElement.sidebar_webhooks]: false,
        [UIElement.sidebar_wireTransfers]: false,
        [UIElement.sidebar_inventory]: false,
        [UIElement.sidebar_otpDevices]: false,
        [UIElement.action_createOrderManually]: false,
        [UIElement.option_advanceInstanceSettings]: false,
        [UIElement.option_advanceOrderCreation]: false,
        [UIElement.option_otpDevicesOnTemplate]: false,
        [UIElement.option_paymentTimeoutOnTemplate]: false,
        [UIElement.option_ageRestriction]: false,
        [UIElement.action_useRevenueApi]: false,
        [UIElement.option_inventoryTaxes]: false,
        [UIElement.sidebar_statistics]: false,
        [UIElement.option_refreshableScopes]: false,
        [UIElement.option_advanceProductTaxes]: false,
        [UIElement.option_exraWireSubject]: false,
      };
    // case "inperson-vending-with-inventory":
    case "point-of-sale":
      return {
        [UIElement.sidebar_orders]: true,
        [UIElement.sidebar_templates]: true,
        [UIElement.sidebar_inventory]: true,
        [UIElement.sidebar_categories]: true,
        [UIElement.sidebar_group]: false,
        [UIElement.sidebar_pots]: false,
        [UIElement.sidebar_reports]: false,
        [UIElement.sidebar_accessTokens]: false,
        [UIElement.sidebar_kycStatus]: true,
        [UIElement.sidebar_bankAccounts]: true,
        [UIElement.sidebar_settings]: true,
        [UIElement.sidebar_password]: true,

        [UIElement.sidebar_discounts]: false,
        [UIElement.sidebar_subscriptions]: false,
        [UIElement.sidebar_tokenFamilies]: false,
        [UIElement.sidebar_webhooks]: false,
        [UIElement.sidebar_wireTransfers]: false,
        [UIElement.sidebar_otpDevices]: false,
        [UIElement.action_createOrderManually]: false,
        [UIElement.option_advanceInstanceSettings]: false,
        [UIElement.option_advanceOrderCreation]: false,
        [UIElement.option_otpDevicesOnTemplate]: false,
        [UIElement.option_paymentTimeoutOnTemplate]: false,
        [UIElement.option_ageRestriction]: false,
        [UIElement.action_useRevenueApi]: false,
        [UIElement.option_inventoryTaxes]: false,
        [UIElement.sidebar_statistics]: false,
        [UIElement.option_refreshableScopes]: false,
        [UIElement.option_advanceProductTaxes]: false,
        [UIElement.option_exraWireSubject]: false,
      };
    case "digital-publishing":
      return {
        [UIElement.sidebar_orders]: true,
        [UIElement.sidebar_accessTokens]: false,
        [UIElement.sidebar_kycStatus]: true,
        [UIElement.sidebar_bankAccounts]: true,
        [UIElement.sidebar_settings]: true,
        [UIElement.sidebar_password]: true,
        [UIElement.sidebar_statistics]: false,

        [UIElement.sidebar_templates]: false,
        [UIElement.sidebar_categories]: false,
        [UIElement.sidebar_group]: false,
        [UIElement.sidebar_pots]: false,
        [UIElement.sidebar_reports]: false,
        [UIElement.sidebar_discounts]: false,
        [UIElement.sidebar_subscriptions]: false,
        [UIElement.sidebar_tokenFamilies]: false,
        [UIElement.sidebar_webhooks]: false,
        [UIElement.sidebar_wireTransfers]: false,
        [UIElement.sidebar_inventory]: false,
        [UIElement.sidebar_otpDevices]: false,
        [UIElement.action_createOrderManually]: false,
        [UIElement.option_advanceInstanceSettings]: false,
        [UIElement.option_advanceOrderCreation]: false,
        [UIElement.option_otpDevicesOnTemplate]: false,
        [UIElement.option_paymentTimeoutOnTemplate]: false,
        [UIElement.option_ageRestriction]: false,
        [UIElement.action_useRevenueApi]: false,
        [UIElement.option_inventoryTaxes]: false,
        [UIElement.option_refreshableScopes]: false,
        [UIElement.option_advanceProductTaxes]: false,
        [UIElement.option_exraWireSubject]: false,
      };
    case "e-commerce":
      return {
        [UIElement.sidebar_orders]: true,
        [UIElement.sidebar_webhooks]: true,
        [UIElement.sidebar_accessTokens]: false,
        [UIElement.sidebar_kycStatus]: true,
        [UIElement.sidebar_bankAccounts]: true,
        [UIElement.sidebar_settings]: true,
        [UIElement.sidebar_password]: true,
        [UIElement.sidebar_statistics]: false,

        [UIElement.sidebar_templates]: false,
        [UIElement.sidebar_categories]: false,
        [UIElement.sidebar_group]: false,
        [UIElement.sidebar_pots]: false,
        [UIElement.sidebar_reports]: false,
        [UIElement.sidebar_discounts]: false,
        [UIElement.sidebar_subscriptions]: false,
        [UIElement.sidebar_tokenFamilies]: false,
        [UIElement.sidebar_wireTransfers]: false,
        [UIElement.sidebar_inventory]: false,
        [UIElement.sidebar_otpDevices]: false,
        [UIElement.action_createOrderManually]: false,
        [UIElement.option_advanceInstanceSettings]: false,
        [UIElement.option_advanceOrderCreation]: false,
        [UIElement.option_otpDevicesOnTemplate]: false,
        [UIElement.option_paymentTimeoutOnTemplate]: false,
        [UIElement.option_ageRestriction]: false,
        [UIElement.action_useRevenueApi]: false,
        [UIElement.option_inventoryTaxes]: false,
        [UIElement.option_refreshableScopes]: false,
        [UIElement.option_advanceProductTaxes]: false,
        [UIElement.option_exraWireSubject]: false,
      };
  }
}

export function HtmlPersonaFlag<T extends keyof h.JSX.IntrinsicElements>(
  props: {
    htmlElement: T;
    point: UIElement;
    children: ComponentChildren;
  } & h.JSX.IntrinsicElements[T],
): VNode | null {
  const { htmlElement: el, children, point, ...rest } = props;
  const [{ persona }] = usePreference();
  const { config } = useSessionContext();
  const isEnabled = getAvailableForPersona(persona ?? config.default_persona)[
    point
  ];
  if (isEnabled) return h(el as any, rest as any, children);
  return null;
}

export function ComponentPersonaFlag<FN extends (props: P) => VNode, P>(
  props: {
    Comp: FN;
    point: UIElement;
    children: ComponentChildren;
  } & P,
): VNode | null {
  const { children, point, Comp, ...rest } = props;
  const [{ persona }] = usePreference();
  const { config } = useSessionContext();
  const isEnabled = getAvailableForPersona(persona ?? config.default_persona)[
    point
  ];
  const d = rest as any;
  if (isEnabled) return <Comp {...d}>{children}</Comp>;
  return null;
}

export function FragmentPersonaFlag(props: {
  point: UIElement;
  showAnywayIf?: boolean;
  children: ComponentChildren;
}): VNode | null {
  const { children, point } = props;
  const [{ persona }] = usePreference();
  const { config } = useSessionContext();
  const isEnabled = getAvailableForPersona(persona ?? config.default_persona)[
    point
  ];
  if (props.showAnywayIf === true || isEnabled)
    return <Fragment>{children}</Fragment>;
  return null;
}
